"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CurrentAncestorsProvider = exports.CurrentAncestorsContext = void 0;
const tslib_1 = require("tslib");
const client_utils_1 = require("@sensenet/client-utils");
const react_1 = tslib_1.__importStar(require("react"));
const semaphore_async_await_1 = tslib_1.__importDefault(require("semaphore-async-await"));
const hooks_1 = require("../hooks");
const current_content_1 = require("./current-content");
/**
 * Context that will return with the current content's ancestors
 */
exports.CurrentAncestorsContext = react_1.default.createContext([]);
exports.CurrentAncestorsContext.displayName = 'CurrentAncestorsContext';
/**
 * Provider component for the CurrentAncestorsContext component.
 * Loads an ancestor list from the Repository. Has to be wrapped with a **CurrentContentContext** and a **RepositoryContext**
 */
const CurrentAncestorsProvider = (props) => {
    const currentContent = react_1.useContext(current_content_1.CurrentContentContext);
    const [loadLock] = react_1.useState(new semaphore_async_await_1.default(1));
    const [ancestors, setAncestors] = react_1.useState([]);
    const repo = hooks_1.useRepository();
    const eventHub = hooks_1.useRepositoryEvents();
    const [reloadToken, setReloadToken] = react_1.useState(1);
    const logger = hooks_1.useLogger('CurrentAncestorsProvider');
    const requestReload = client_utils_1.debounce((newId) => {
        if (ancestors.map((a) => a.Id).includes(newId)) {
            setReloadToken(Math.random());
        }
    }, 100);
    react_1.useEffect(() => {
        const subscriptions = [
            eventHub.onContentModified.subscribe((mod) => {
                requestReload(mod.content.Id);
            }),
            eventHub.onContentMoved.subscribe((moved) => {
                moved.content.forEach((movedContent) => requestReload(movedContent.Id));
            }),
            eventHub.onContentDeleted.subscribe((del) => {
                del.contentData.forEach((deletedContent) => requestReload(deletedContent.Id));
            }),
        ];
        return () => subscriptions.forEach((s) => s.dispose());
    }, [
        ancestors,
        eventHub.onContentDeleted,
        eventHub.onContentModified,
        eventHub.onContentMoved,
        repo.configuration.repositoryUrl,
        requestReload,
    ]);
    const [error, setError] = react_1.useState();
    react_1.useEffect(() => {
        const ac = new AbortController();
        (() => tslib_1.__awaiter(void 0, void 0, void 0, function* () {
            try {
                yield loadLock.acquire();
                if ((props.root && currentContent.Id === props.root) || currentContent.Path === props.root) {
                    setAncestors([]);
                }
                else if (currentContent.Id) {
                    const ancestorsResult = yield repo.executeAction({
                        idOrPath: currentContent.Id,
                        method: 'GET',
                        name: 'Ancestors',
                        body: undefined,
                        requestInit: {
                            signal: ac.signal,
                        },
                        oDataOptions: {
                            orderby: [['Path', 'asc']],
                        },
                    });
                    const rootIndex = ancestorsResult.d.results.findIndex((a) => a.Id === props.root || a.Path === props.root);
                    setAncestors(rootIndex > 0 ? ancestorsResult.d.results.slice(rootIndex) : ancestorsResult.d.results);
                }
            }
            catch (err) {
                if (!ac.signal.aborted) {
                    setError(err);
                }
            }
            finally {
                loadLock.release();
            }
        }))();
        return () => ac.abort();
    }, [currentContent.Id, currentContent.Path, loadLock, props.root, reloadToken, repo]);
    if (error) {
        logger.warning({
            message: `Error loading ancestors. ${error.message}`,
            data: { error, relatedContent: currentContent, relatedRepository: repo.configuration.repositoryUrl },
        });
    }
    return react_1.default.createElement(exports.CurrentAncestorsContext.Provider, { value: ancestors }, props.children);
};
exports.CurrentAncestorsProvider = CurrentAncestorsProvider;
//# sourceMappingURL=current-ancestors.js.map